import React, { useState } from 'react';
import { FileText, Clock, Users, TrendingUp, Plus, Edit, Calendar, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';

interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  sections: string[];
  schedule?: {
    frequency: 'daily' | 'weekly' | 'monthly';
    time: string;
    recipients: string[];
  };
  format: 'pdf' | 'excel' | 'csv';
  lastRun?: Date;
  nextRun?: Date;
  isActive: boolean;
}

interface ReportTemplatesProps {
  templates: ReportTemplate[];
  onUseTemplate: (template: ReportTemplate) => void;
  onEditTemplate: (template: ReportTemplate) => void;
  onScheduleTemplate: (template: ReportTemplate) => void;
  onCreateTemplate: (template: Partial<ReportTemplate>) => void;
}

const defaultTemplates: ReportTemplate[] = [
  {
    id: 'daily-fraud-summary',
    name: 'Daily Fraud Summary',
    description: 'Comprehensive daily report of all fraud detection activity',
    sections: [
      'Executive Summary',
      'Fraud Metrics',
      'Pattern Analysis',
      'Financial Impact',
      'Detailed Lead List'
    ],
    schedule: {
      frequency: 'daily',
      time: '09:00',
      recipients: ['team@company.com', 'management@company.com']
    },
    format: 'pdf',
    lastRun: new Date(Date.now() - 24 * 60 * 60 * 1000),
    nextRun: new Date(Date.now() + 15 * 60 * 1000),
    isActive: true
  },
  {
    id: 'weekly-analytics',
    name: 'Weekly Analytics Report',
    description: 'Week-over-week comparison and trend analysis',
    sections: [
      'Week-over-week comparison',
      'Top fraud sources',
      'Pattern trends',
      'ML model performance',
      'Recommendations'
    ],
    schedule: {
      frequency: 'weekly',
      time: '08:00',
      recipients: ['analytics@company.com']
    },
    format: 'excel',
    lastRun: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
    nextRun: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000),
    isActive: true
  },
  {
    id: 'partner-performance',
    name: 'Partner Performance Report',
    description: 'Fraud rates by lead source/partner with quality scores',
    sections: [
      'Partner fraud rates',
      'Cost analysis',
      'Quality scores',
      'Volume trends',
      'Recommendations'
    ],
    format: 'pdf',
    isActive: false
  },
  {
    id: 'executive-monthly',
    name: 'Monthly Executive Report',
    description: 'High-level summary for executive stakeholders',
    sections: [
      'Monthly overview',
      'ROI analysis',
      'Key achievements',
      'Risk assessment',
      'Strategic recommendations'
    ],
    schedule: {
      frequency: 'monthly',
      time: '07:00',
      recipients: ['executives@company.com']
    },
    format: 'pdf',
    nextRun: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000),
    isActive: true
  }
];

export function ReportTemplates({ 
  templates = defaultTemplates, 
  onUseTemplate, 
  onEditTemplate, 
  onScheduleTemplate,
  onCreateTemplate 
}: ReportTemplatesProps) {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newTemplate, setNewTemplate] = useState<Partial<ReportTemplate>>({
    name: '',
    description: '',
    sections: [],
    format: 'pdf',
    isActive: false
  });

  const availableSections = [
    'Executive Summary',
    'Fraud Metrics', 
    'Pattern Analysis',
    'Financial Impact',
    'Detailed Lead List',
    'Geographic Analysis',
    'Time-based Analysis',
    'Source Performance',
    'ML Model Performance',
    'Comparative Analysis',
    'Risk Assessment',
    'Recommendations'
  ];

  const formatNextRun = (date?: Date) => {
    if (!date) return 'Not scheduled';
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    
    if (diffMs < 0) return 'Overdue';
    
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) {
      return `In ${diffDays} day${diffDays > 1 ? 's' : ''}`;
    } else if (diffHours > 0) {
      return `In ${diffHours} hour${diffHours > 1 ? 's' : ''}`;
    } else {
      return 'Soon';
    }
  };

  const handleCreateTemplate = () => {
    onCreateTemplate(newTemplate);
    setNewTemplate({
      name: '',
      description: '',
      sections: [],
      format: 'pdf',
      isActive: false
    });
    setIsCreateDialogOpen(false);
  };

  const handleSectionToggle = (section: string, checked: boolean) => {
    setNewTemplate(prev => ({
      ...prev,
      sections: checked 
        ? [...(prev.sections || []), section]
        : (prev.sections || []).filter(s => s !== section)
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Report Templates</h2>
          <p className="text-muted-foreground">
            Pre-configured reports for different stakeholders and use cases
          </p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Create Template
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create Report Template</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="template-name">Template Name</Label>
                <Input
                  id="template-name"
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter template name"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="template-description">Description</Label>
                <Textarea
                  id="template-description"
                  value={newTemplate.description}
                  onChange={(e) => setNewTemplate(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe what this report includes"
                />
              </div>
              
              <div className="space-y-3">
                <Label>Report Sections</Label>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  {availableSections.map((section) => (
                    <div key={section} className="flex items-center space-x-2">
                      <Checkbox
                        id={section}
                        checked={(newTemplate.sections || []).includes(section)}
                        onCheckedChange={(checked) => handleSectionToggle(section, checked as boolean)}
                      />
                      <Label htmlFor={section} className="text-sm">
                        {section}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Export Format</Label>
                <Select
                  value={newTemplate.format}
                  onValueChange={(value: 'pdf' | 'excel' | 'csv') => 
                    setNewTemplate(prev => ({ ...prev, format: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pdf">PDF Report</SelectItem>
                    <SelectItem value="excel">Excel Workbook</SelectItem>
                    <SelectItem value="csv">CSV Data</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateTemplate}>
                  Create Template
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template) => (
          <Card key={template.id} className="relative">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <CardTitle className="text-lg">{template.name}</CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge variant={template.isActive ? 'default' : 'secondary'}>
                      {template.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {template.format.toUpperCase()}
                    </Badge>
                  </div>
                </div>
                {template.schedule && (
                  <div className="text-xs text-muted-foreground">
                    <Clock className="h-3 w-3 inline mr-1" />
                    {template.schedule.frequency}
                  </div>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {template.description}
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs font-medium text-muted-foreground">SECTIONS</Label>
                <div className="mt-1 space-y-1">
                  {template.sections.map((section) => (
                    <div key={section} className="text-sm flex items-center">
                      <span className="text-green-500 mr-2">✓</span>
                      {section}
                    </div>
                  ))}
                </div>
              </div>
              
              {template.schedule && (
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-muted-foreground">SCHEDULE</Label>
                  <div className="text-sm">
                    <div>Next run: {formatNextRun(template.nextRun)}</div>
                    <div className="text-muted-foreground">
                      {template.schedule.recipients.length} recipient(s)
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex gap-2 pt-2">
                <Button size="sm" onClick={() => onUseTemplate(template)}>
                  <Play className="h-3 w-3 mr-1" />
                  Use
                </Button>
                <Button size="sm" variant="outline" onClick={() => onEditTemplate(template)}>
                  <Edit className="h-3 w-3 mr-1" />
                  Edit
                </Button>
                <Button size="sm" variant="outline" onClick={() => onScheduleTemplate(template)}>
                  <Calendar className="h-3 w-3 mr-1" />
                  Schedule
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}